// Куда попадает человек сразу после входа. Главный сценарий продукта: утро финансового
// директора — ноль кликов до подотчёта, отсортированного по давности (ПРАВИЛА.md, 1.1).

import { visibleSections, can, defaultViewFor } from '@/shared/rbac/permissions';

interface StartRouteUser {
  roles: readonly string[];
  permissions: readonly string[];
  /** Карточка сотрудника этого человека. Без неё «своё» показать нечего. */
  employee_id?: string | null;
}

/**
 * Стартовый маршрут по роли. finance и admin открывают «Деньги → Подотчёт»; остальные —
 * первый доступный им раздел. null — не доступно ничего: каркас показывает честное
 * объяснение вместо пустого экрана или петли на /login.
 */
export function startRoute(user: StartRouteUser): string | null {
  const financeLike = user.roles.includes('finance') || user.roles.includes('admin');
  if (financeLike && can(user.permissions, 'core.accountable.view')) {
    return '/money/accountable';
  }
  // Утро генерального директора. Он в системе смотрит, а не работает: подписывает, выдаёт
  // деньги и решает по спорному. Первый его вопрос — «что с компанией», а не «у кого висит
  // подотчёт», и до 08.09.2026 экрана с этим ответом у него не было вовсе: роль проваливалась
  // в ветку «первый доступный раздел» и открывала рабочий пульт бухгалтерии.
  //
  // Восьмого раздела ради этого не заводим — потолок разделов уже превышен на один
  // (`ПРАВИЛА.md`, правило 12). Домом становится существующая сводка за месяц: сколько стоит
  // месяц и как менялось за год, где не хватает людей, кому ещё не перевели, что теряем
  // безвозвратно.
  if (user.roles.includes('management') && can(user.permissions, 'core.payroll.view')) {
    return '/payroll/summary';
  }
  // Утро операционного руководства — это заместитель по физической охране. Его работа в
  // системе названа в матрице тз одной строкой: второе согласование ведомости перед выплатой,
  // проверка премий и изменений тарифов. Подотчёта он не видит вовсе (`roles.yaml`,
  // hidden_modules: accountable), поэтому до 15.09.2026 первым доступным разделом у него
  // оказывались «Люди» — реестр на 281 строку, где ему нечего решать, а до собственной кнопки
  // оставалось три нажатия.
  //
  // Право на ведомости проверяется вместе с правом на второе согласование: согласовывать, не
  // видя ведомости, нельзя, и без первого экран встретил бы его отказом прямо на входе.
  if (
    user.roles.includes('operations') &&
    can(user.permissions, 'core.payroll.second_approve') &&
    can(user.permissions, 'core.payroll.view')
  ) {
    return '/payroll/sheets';
  }
  // Утро рекрутёра. Единственная роль, у которой в кабинете нет ни одного денежного права, —
  // и до 15.09.2026 её встречала денежная сводка за месяц: раздел «Расчёт» открывался ему по
  // праву на панель показателей, а «Сводка» была там первым представлением, до чьего права он
  // дотянулся. Внутри по его правам оставался один блок из семи, укомплектованность, и тот
  // считался по объектам — а запрос объектов у рекрутёра выключен отсутствием
  // `core.dictionary.view`. То есть человек входил в программу и видел экран, который не
  // отвечал ему ни на один вопрос.
  if (user.roles.includes('recruiter') && can(user.permissions, 'hiring.candidate.view')) {
    return '/hiring/candidates';
  }
  // Утро табельщика: его работа — табель, и вести его через два экрана к ней незачем. Раньше
  // первым доступным разделом у него оказывались «Люди», а «Расчёт» он не видел вовсе.
  if (user.roles.includes('timekeeper') && can(user.permissions, 'core.timesheet.view')) {
    return '/payroll/timesheet';
  }
  // Утро охранника. Тз, раздел 3.1, отводит ему в системе ровно одно: собственный расчёт —
  // часы против нормы, ставка, каждое удержание с остатком долга, сумма на руки. Всё, что он
  // может делать в кабинете, — это смотреть свой лист.
  //
  // До 02.09.2026 он попадал на пульт подотчёта, потому что владелец 23.08 выдал ему право
  // видеть подотчёт (ради собственных заявок в приложении). Прогон ролей показал, чем это
  // оборачивается в кабинете: «0 подотчётных лиц» и обещание «строка появится, как только
  // финотдел оформит первую выдачу» — обещание, которого для охранника не будет никогда,
  // потому что подотчёт ему по матрице не заводят вовсе. Экран отвечал на вопрос, которого
  // он не задавал, а на его собственный вопрос — «сколько мне начислили» — не отвечал.
  //
  // Расчёта за месяц может не быть, и тогда лист скажет об этом словами. Это верный ответ:
  // человек узнаёт, что ведомость ещё не собрали, вместо того чтобы искать себя в чужом списке.
  const guardOnly =
    user.roles.length === 1 && user.roles[0] === 'guard' && user.employee_id != null;
  if (guardOnly && can(user.permissions, 'core.payslip.view_own')) {
    return `/people/employees/r/${user.employee_id!}?tab=payslip`;
  }

  const first = visibleSections(user.permissions)[0];
  return first === undefined ? null : `/${first.slug}/${defaultViewFor(first, user.permissions)}`;
}
